import React, { useState, useEffect } from "react";
import { supabase } from "../utils/supabase";
import { useAuth } from "../contexts/AuthContext";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ArrowTopRightOnSquareIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XMarkIcon,
  DocumentTextIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { format, parseISO, differenceInDays } from "date-fns";
import { nl } from "date-fns/locale";
import toast from "react-hot-toast";
import { logAuditEvent } from "../utils/auditLog";
import ConfirmDialog from "../components/ConfirmDialog";

interface Document {
  id: string;
  pharmacy_id: string;
  name: string;
  description?: string;
  category: "sop" | "werkwijze" | "certificaat" | "beleid" | "formulier" | "other";
  drive_url: string;
  version?: string;
  owner_id?: string;
  owner_name?: string;
  review_date?: string;
  last_reviewed_by_id?: string;
  last_reviewed_by_name?: string;
  last_reviewed_at?: string;
  notes?: string;
  created_by: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
}

type DocumentStatus = "current" | "review_due" | "expired";

const CATEGORY_LABELS: Record<Document["category"], string> = {
  sop: "SOP",
  werkwijze: "Werkwijze",
  certificaat: "Certificaat",
  beleid: "Beleid",
  formulier: "Formulier",
  other: "Overig",
};

const CATEGORY_COLORS: Record<Document["category"], string> = {
  sop: "bg-blue-100 text-blue-800",
  werkwijze: "bg-purple-100 text-purple-800",
  certificaat: "bg-green-100 text-green-800",
  beleid: "bg-orange-100 text-orange-800",
  formulier: "bg-gray-100 text-gray-800",
  other: "bg-gray-100 text-gray-600",
};

const emptyForm = {
  name: "",
  description: "",
  category: "sop" as Document["category"],
  drive_url: "",
  version: "",
  owner_id: "",
  review_date: "",
  notes: "",
};

function getDocumentStatus(reviewDate?: string): DocumentStatus {
  if (!reviewDate) return "current";
  const days = differenceInDays(parseISO(reviewDate), new Date());
  if (days < 0) return "expired";
  if (days <= 30) return "review_due";
  return "current";
}

function StatusBadge({ reviewDate }: { reviewDate?: string }) {
  const status = getDocumentStatus(reviewDate);
  if (status === "expired") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <ExclamationTriangleIcon className="h-3 w-3" />
        Verlopen
      </span>
    );
  }
  if (status === "review_due") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
        <ClockIcon className="h-3 w-3" />
        Beoordeling nodig
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
      <CheckCircleIcon className="h-3 w-3" />
      Actueel
    </span>
  );
}

const Documents: React.FC = () => {
  const { user, selectedPharmacy } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [users, setUsers] = useState<{ id: string; full_name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState<Document | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  useEffect(() => {
    if (selectedPharmacy) {
      fetchDocuments();
      fetchUsers();
    }
  }, [selectedPharmacy]);

  const fetchDocuments = async () => {
    if (!selectedPharmacy) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .eq("pharmacy_id", selectedPharmacy.id)
        .order("name", { ascending: true });
      if (error) throw error;
      setDocuments(data ?? []);
    } catch (err) {
      console.error("Error fetching documents:", err);
      toast.error("Fout bij laden van documenten");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (!selectedPharmacy) return;
    const { data } = await supabase
      .from("users")
      .select("id, full_name")
      .eq("pharmacy_id", selectedPharmacy.id)
      .order("full_name");
    setUsers(data ?? []);
  };

  const openCreate = () => {
    setEditingDoc(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (doc: Document) => {
    setEditingDoc(doc);
    setForm({
      name: doc.name,
      description: doc.description ?? "",
      category: doc.category,
      drive_url: doc.drive_url,
      version: doc.version ?? "",
      owner_id: doc.owner_id ?? "",
      review_date: doc.review_date ?? "",
      notes: doc.notes ?? "",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!user || !selectedPharmacy) return;
    if (!form.name.trim() || !form.drive_url.trim()) {
      toast.error("Naam en Drive-link zijn verplicht");
      return;
    }
    setSaving(true);
    try {
      const ownerUser = form.owner_id ? users.find((u) => u.id === form.owner_id) : null;
      const payload = {
        pharmacy_id: selectedPharmacy.id,
        name: form.name.trim(),
        description: form.description || null,
        category: form.category,
        drive_url: form.drive_url.trim(),
        version: form.version || null,
        owner_id: form.owner_id || null,
        owner_name: ownerUser?.full_name ?? null,
        review_date: form.review_date || null,
        notes: form.notes || null,
        updated_at: new Date().toISOString(),
      };

      if (editingDoc) {
        const { error } = await supabase
          .from("documents")
          .update(payload)
          .eq("id", editingDoc.id);
        if (error) throw error;
        toast.success("Document bijgewerkt");
      } else {
        const { error } = await supabase
          .from("documents")
          .insert({ ...payload, created_by: user.id, created_by_name: user.full_name });
        if (error) throw error;
        toast.success("Document toegevoegd");
      }

      setShowModal(false);
      fetchDocuments();
    } catch (err) {
      console.error("Error saving document:", err);
      toast.error("Fout bij opslaan");
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReviewed = async (doc: Document) => {
    if (!user || !selectedPharmacy) return;
    try {
      // Advance review date by 1 year from today
      const nextReview = new Date();
      nextReview.setFullYear(nextReview.getFullYear() + 1);
      const nextReviewStr = format(nextReview, "yyyy-MM-dd");

      const { error } = await supabase
        .from("documents")
        .update({
          last_reviewed_by_id: user.id,
          last_reviewed_by_name: user.full_name,
          last_reviewed_at: new Date().toISOString(),
          review_date: nextReviewStr,
          updated_at: new Date().toISOString(),
        })
        .eq("id", doc.id);

      if (error) throw error;

      toast.success("Document gemarkeerd als beoordeeld — volgende beoordeling over 1 jaar");

      await logAuditEvent({
        pharmacy_id: selectedPharmacy.id,
        user_id: user.id,
        user_name: user.full_name,
        action: "approved",
        entity_type: "document",
        entity_id: doc.id,
        entity_label: doc.name,
        details: { next_review: nextReviewStr },
      });

      fetchDocuments();
    } catch (err) {
      console.error("Error marking reviewed:", err);
      toast.error("Fout bij bijwerken");
    }
  };

  const handleDelete = async (docId: string) => {
    try {
      const { error } = await supabase
        .from("documents")
        .delete()
        .eq("id", docId);
      if (error) throw error;
      toast.success("Document verwijderd");
      fetchDocuments();
    } catch (err) {
      console.error("Error deleting document:", err);
      toast.error("Fout bij verwijderen");
    } finally {
      setConfirmDelete(null);
    }
  };

  const filtered = documents.filter((doc) => {
    if (categoryFilter !== "all" && doc.category !== categoryFilter) return false;
    if (statusFilter !== "all" && getDocumentStatus(doc.review_date) !== statusFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        doc.name.toLowerCase().includes(q) ||
        (doc.description ?? "").toLowerCase().includes(q) ||
        (doc.owner_name ?? "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const canEdit = user?.role === "admin" || user?.role === "manager";

  return (
    <div className="p-6 sm:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documentenbeheer</h1>
          <p className="text-gray-600 mt-1">
            Kwaliteitsdocumenten gekoppeld aan Google Drive
          </p>
        </div>
        {canEdit && (
          <button
            onClick={openCreate}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Document toevoegen
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Zoek op naam, omschrijving, eigenaar…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">Alle categorieën</option>
            {(Object.keys(CATEGORY_LABELS) as Document["category"][]).map((c) => (
              <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">Alle statussen</option>
            <option value="current">Actueel</option>
            <option value="review_due">Beoordeling nodig</option>
            <option value="expired">Verlopen</option>
          </select>
        </div>
      </div>

      {/* Summary pills */}
      {!loading && (
        <div className="flex gap-3 mb-4 flex-wrap">
          {(["current", "review_due", "expired"] as DocumentStatus[]).map((s) => {
            const count = documents.filter((d) => getDocumentStatus(d.review_date) === s).length;
            const labels = { current: "Actueel", review_due: "Beoordeling nodig", expired: "Verlopen" };
            const colors = {
              current: "bg-green-50 text-green-700 border-green-200",
              review_due: "bg-amber-50 text-amber-700 border-amber-200",
              expired: "bg-red-50 text-red-700 border-red-200",
            };
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(statusFilter === s ? "all" : s)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${colors[s]} ${statusFilter === s ? "ring-2 ring-offset-1 ring-current" : ""}`}
              >
                {count} {labels[s]}
              </button>
            );
          })}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Laden…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center">
            <DocumentTextIcon className="mx-auto h-10 w-10 text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">Geen documenten gevonden.</p>
            {canEdit && (
              <button onClick={openCreate} className="mt-3 text-primary-600 text-sm hover:underline">
                Voeg het eerste document toe
              </button>
            )}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categorie</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Eigenaar</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beoordeling</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acties</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filtered.map((doc) => {
                const status = getDocumentStatus(doc.review_date);
                const daysLeft = doc.review_date
                  ? differenceInDays(parseISO(doc.review_date), new Date())
                  : null;
                return (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <DocumentTextIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                          {doc.description && (
                            <p className="text-xs text-gray-500 truncate max-w-xs">{doc.description}</p>
                          )}
                          {doc.version && (
                            <p className="text-xs text-gray-400">v{doc.version}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${CATEGORY_COLORS[doc.category]}`}>
                        {CATEGORY_LABELS[doc.category]}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {doc.owner_name ?? "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {doc.review_date ? (
                        <div>
                          <p className={`text-sm ${status === "expired" ? "text-red-600 font-medium" : status === "review_due" ? "text-amber-600 font-medium" : "text-gray-700"}`}>
                            {format(parseISO(doc.review_date), "d MMM yyyy", { locale: nl })}
                          </p>
                          {daysLeft !== null && (
                            <p className="text-xs text-gray-400">
                              {daysLeft < 0
                                ? `${Math.abs(daysLeft)} dagen geleden`
                                : daysLeft === 0
                                ? "Vandaag"
                                : `Over ${daysLeft} dagen`}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <StatusBadge reviewDate={doc.review_date} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {/* Open in Drive */}
                        <a
                          href={doc.drive_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                          title="Openen in Google Drive"
                        >
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </a>
                        {/* Mark reviewed */}
                        {(status === "expired" || status === "review_due") && (
                          <button
                            onClick={() => handleMarkReviewed(doc)}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                            title="Markeer als beoordeeld"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                          </button>
                        )}
                        {/* Edit */}
                        {canEdit && (
                          <button
                            onClick={() => openEdit(doc)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                            title="Bewerken"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                        {/* Delete */}
                        {canEdit && (
                          <button
                            onClick={() => setConfirmDelete(doc.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Verwijderen"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!loading && (
        <p className="mt-3 text-xs text-gray-400 text-right">
          {filtered.length} van {documents.length} documenten
        </p>
      )}

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingDoc ? "Document bewerken" : "Document toevoegen"}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Naam *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="bv. SOP Magistrale Bereiding"
                  className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Google Drive link *</label>
                <input
                  type="url"
                  value={form.drive_url}
                  onChange={(e) => setForm((f) => ({ ...f, drive_url: e.target.value }))}
                  placeholder="https://docs.google.com/…"
                  className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="mt-1 text-xs text-gray-400">Plak de deellink van het Google Doc, Sheet, Slide of Drive-bestand.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categorie</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as Document["category"] }))}
                    className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    {(Object.keys(CATEGORY_LABELS) as Document["category"][]).map((c) => (
                      <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Versie</label>
                  <input
                    type="text"
                    value={form.version}
                    onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))}
                    placeholder="bv. 3.2"
                    className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eigenaar</label>
                  <select
                    value={form.owner_id}
                    onChange={(e) => setForm((f) => ({ ...f, owner_id: e.target.value }))}
                    className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">— Selecteer —</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.full_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Beoordelingsdatum</label>
                  <input
                    type="date"
                    value={form.review_date}
                    onChange={(e) => setForm((f) => ({ ...f, review_date: e.target.value }))}
                    className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Omschrijving</label>
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Korte omschrijving van het document…"
                  className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notities</label>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Annuleren
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? "Opslaan…" : editingDoc ? "Bijwerken" : "Toevoegen"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDelete !== null}
        title="Document verwijderen"
        message="Weet je zeker dat je dit document wilt verwijderen? Het document in Google Drive wordt niet verwijderd."
        confirmLabel="Verwijderen"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default Documents;
